import {
  CompleteAssessmentAnswerInput,
  AssessmentAnswer,
} from '../../generated/graphql';
import {
  FormQuestion,
  ProgramAssessmentQuestion,
  PatientContactNewDraft,
  PATIENT_CONTACT_NEW_SENTINEL,
  mapAssessmentAnswersToMutationInput,
} from './program-assessment';
import { CompleteProviderFormData } from '../pages/CompleteProviderForm/helpers';
import { DateTime } from 'luxon';
import {
  isMultipleChoiceAssessmentAnswer,
  isMultipleChoiceScoredAssessmentAnswer,
  isMultiSelectAssessmentAnswer,
  isFreeTextAssessmentAnswer,
  isRichTextAssessmentAnswer,
  isSignatureAssessmentAnswer,
  isInitialsAssessmentAnswer,
  isFileUploadAssessmentAnswer,
  isAutofillAssessmentAnswer,
  isDatePickerAssessmentAnswer,
  isPatientContactAssessmentAnswer,
  isProviderSignatureAssessmentAnswer,
  isMultiSignatureAssessmentAnswer,
} from './assessments';
import {
  getAnswerValue,
  getAnswerValues,
  getAnswerQuestion,
} from './answerHelpers';

/**
 * Converts react-hook-form values to GraphQL draft format.
 * Handles both single form mode and multi-form mode.
 *
 * @param formValues - The form values from react-hook-form
 * @param questions - The assessment questions for mapping answers
 * @param programAssessmentId - The ID of the program assessment (used for multi-form mode)
 * @returns Array of draft answer inputs for GraphQL mutation
 */
export const formValuesToDraftInput = (
  formValues: CompleteProviderFormData,
  questions: FormQuestion[],
  programAssessmentId: string,
): CompleteAssessmentAnswerInput[] => {
  // Handle multi-form mode
  if (formValues.forms && formValues.forms[programAssessmentId]) {
    const formData = formValues.forms[programAssessmentId];
    return mapAssessmentAnswersToMutationInput(questions, formData.answers);
  }

  // Handle single form mode
  if (formValues.answers) {
    return mapAssessmentAnswersToMutationInput(questions, formValues.answers);
  }

  return [];
};

/**
 * Formats "last saved" timestamp for display.
 * Returns human-readable relative time (e.g., "Saved 5 seconds ago", "Saved 2 minutes ago").
 *
 * @param lastSaved - The date when the form was last saved, or null if never saved
 * @returns Formatted string for display, or empty string if lastSaved is null
 */
export const formatLastSavedTime = (lastSaved: Date | null): string => {
  if (!lastSaved) return '';

  const now = DateTime.now();
  const saved = DateTime.fromJSDate(lastSaved);
  const diff = now.diff(saved, ['seconds', 'minutes', 'hours']);

  if (diff.seconds < 60) {
    return 'Saved just now';
  } else if (diff.minutes < 60) {
    const mins = Math.floor(diff.minutes);
    return `Saved ${mins} minute${mins > 1 ? 's' : ''} ago`;
  } else {
    return `Saved at ${saved.toLocaleString(DateTime.TIME_SIMPLE)}`;
  }
};

/**
 * Converts a matched AssessmentAnswer into the form answer shape.
 */
const toFormAnswer = (
  answer: AssessmentAnswer,
): CompleteProviderFormData['answers'][number] => {
  if (
    isMultipleChoiceAssessmentAnswer(answer) ||
    isMultipleChoiceScoredAssessmentAnswer(answer)
  ) {
    return { value: answer.answer, values: [] };
  }
  if (isMultiSelectAssessmentAnswer(answer)) {
    return { value: '', values: getAnswerValues(answer) };
  }
  if (
    isFreeTextAssessmentAnswer(answer) ||
    isRichTextAssessmentAnswer(answer)
  ) {
    return { value: answer.answer, values: [] };
  }
  if (
    isSignatureAssessmentAnswer(answer) ||
    isInitialsAssessmentAnswer(answer) ||
    isProviderSignatureAssessmentAnswer(answer)
  ) {
    return { value: answer.signatureName || '', values: [] };
  }
  if (isMultiSignatureAssessmentAnswer(answer)) {
    return { value: '', values: [] };
  }
  if (isFileUploadAssessmentAnswer(answer)) {
    return {
      value: '',
      values: [],
      mediaId: answer.mediaId ?? '',
      fileName: answer.fileName ?? '',
    } as CompleteProviderFormData['answers'][number];
  }
  if (
    isAutofillAssessmentAnswer(answer) ||
    isDatePickerAssessmentAnswer(answer)
  ) {
    return { value: getAnswerValue(answer) ?? '', values: [] };
  }
  if (isPatientContactAssessmentAnswer(answer)) {
    let newContactDraft: PatientContactNewDraft | undefined;
    const draftJson = answer.newContactDraftJson;
    if (draftJson) {
      try {
        newContactDraft = JSON.parse(draftJson);
      } catch (err) {
        console.error(
          `[formDraftHelpers] Failed to parse newContactDraftJson for question "${answer.question}":`,
          err,
        );
      }
    }
    return {
      value: '',
      values: [],
      contactId:
        answer.contactId ??
        (newContactDraft ? PATIENT_CONTACT_NEW_SENTINEL : undefined),
      newContactDraft,
    };
  }
  return { value: '', values: [], contactId: undefined };
};

const emptyAnswer: CompleteProviderFormData['answers'][number] = {
  value: '',
  values: [],
  contactId: undefined,
};

/**
 * Converts AssessmentAnswer from GraphQL to form values.
 *
 * Matches draft answers to questions by text, tracking which draft answers
 * have already been used. This prevents the same answer from being returned
 * for multiple questions that share identical question text.
 */
export const assessmentAnswersToFormValues = (
  draftAnswers: AssessmentAnswer[] | null | undefined,
  questions: FormQuestion[],
): CompleteProviderFormData['answers'] => {
  if (!draftAnswers || !questions || draftAnswers.length === 0) return [];

  const usedDraftIndices = new Set<number>();

  return questions.map((question) => {
    let matchedIdx = -1;
    const answer = draftAnswers.find((a, draftIdx) => {
      if (usedDraftIndices.has(draftIdx)) return false;
      if (getAnswerQuestion(a) === question.question) {
        matchedIdx = draftIdx;
        return true;
      }
      return false;
    });
    if (answer && matchedIdx >= 0) {
      usedDraftIndices.add(matchedIdx);
      return toFormAnswer(answer);
    }
    return { ...emptyAnswer };
  });
};

/**
 * Convert draft answers back to form values (legacy function, kept for compatibility)
 * @deprecated Use assessmentAnswersToFormValues instead
 */
export const draftAnswersToFormValues = (
  draftAnswers: any[],
  questions: ProgramAssessmentQuestion[],
): CompleteProviderFormData['answers'] => {
  return assessmentAnswersToFormValues(
    draftAnswers as AssessmentAnswer[],
    questions,
  );
};
